import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Receipt,
  People,
  Add,
  Refresh,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import salesDashboardService, { SalesDashboardData } from '../services/salesDashboardService';
import { usePermissions } from '../hooks/usePermissions';
import PeriodSelector from '../components/common/PeriodSelector';
import { computePresetRange, DEFAULT_DATE_RANGE_PRESET } from '../utils/dateRangePresets';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const SalesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(() => computePresetRange(DEFAULT_DATE_RANGE_PRESET));
  const [dashboardData, setDashboardData] = useState<SalesDashboardData | null>(null);
  const [error, setError] = useState<{ message: string; isForbidden: boolean } | null>(null);

  const fetchDashboardData = useCallback(async () => {
    // Keep any previously loaded data visible while refetching (period change,
    // manual refresh) — only the very first load shows the full skeleton.
    if (dashboardData) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const data = await salesDashboardService.getDashboardData('custom', dateRange.dateFrom, dateRange.dateTo);
      setDashboardData(data);
    } catch (err: any) {
      const status = err?.response?.status;
      setError({
        message: status === 403
          ? "You don't have permission to view sales analytics."
          : 'Failed to load dashboard data. Please try again.',
        isForbidden: status === 403
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = useMemo(() => {
    return (value: number) => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(value);
    };
  }, []);

  const formatNumber = useMemo(() => {
    return (value: number) => {
      return new Intl.NumberFormat('en-IN').format(value);
    };
  }, []);

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'draft': 'default',
      'pending': 'warning',
      'confirmed': 'info',
      'processing': 'primary',
      'shipped': 'secondary',
      'delivered': 'success',
      'cancelled': 'error'
    };
    return statusColors[status.toLowerCase()] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={240} height={48} sx={{ mb: 2 }} />
        <Grid container spacing={3} mb={3}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box p={3}>
        <Alert severity={error?.isForbidden ? 'warning' : 'error'} action={
          !error?.isForbidden && (
            <Button color="inherit" size="small" onClick={fetchDashboardData}>Retry</Button>
          )
        }>
          {error?.message || 'Failed to load dashboard data.'}
        </Alert>
      </Box>
    );
  }

  const { metrics, revenueChart, topProducts, topCustomers, orderStatusBreakdown, recentOrders } = dashboardData;

  const quickActions = [
    { label: 'New Sales Order', icon: <Add />, variant: 'contained' as const, color: 'primary' as const, path: '/admin/documents/sales-orders/create', permission: 'sales_orders.create' },
    { label: 'New Quotation', icon: <Add />, variant: 'contained' as const, color: 'secondary' as const, path: '/admin/documents/estimates/create', permission: 'estimates.create' },
    { label: 'New Invoice', icon: <Add />, variant: 'contained' as const, color: 'success' as const, path: '/admin/documents/invoices/create', permission: 'invoices.create' },
    { label: 'Add Customer', icon: <Add />, variant: 'outlined' as const, color: 'primary' as const, path: '/admin/parties/customers/create', permission: 'customers.create' },
    { label: 'View All Orders', icon: <ShoppingCart />, variant: 'outlined' as const, color: 'primary' as const, path: '/admin/documents/sales-orders', permission: 'sales_orders.read' },
  ].filter((a) => hasPermission(a.permission));

  return (
    <Box sx={{ p: 3, opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.15s' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Sales Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <PeriodSelector onChange={(dateFrom, dateTo) => setDateRange({ dateFrom, dateTo })} />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchDashboardData} color="primary" disabled={refreshing}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error.message}
        </Alert>
      )}

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Quick Actions</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant={action.variant}
                color={action.color}
                startIcon={action.icon}
                onClick={() => navigate(action.path)}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </Paper>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(metrics.totalRevenue)}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {metrics.revenueGrowth >= 0 ? (
                      <TrendingUp color="success" fontSize="small" />
                    ) : (
                      <TrendingDown color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={metrics.revenueGrowth >= 0 ? 'success.main' : 'error.main'}
                      ml={0.5}
                    >
                      {Math.abs(metrics.revenueGrowth).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <Receipt color="primary" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Total Orders
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatNumber(metrics.totalOrders)}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {metrics.ordersGrowth >= 0 ? (
                      <TrendingUp color="success" fontSize="small" />
                    ) : (
                      <TrendingDown color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={metrics.ordersGrowth >= 0 ? 'success.main' : 'error.main'}
                      ml={0.5}
                    >
                      {Math.abs(metrics.ordersGrowth).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <ShoppingCart color="secondary" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Average Order Value
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(metrics.averageOrderValue)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    Per transaction
                  </Typography>
                </Box>
                <TrendingUp color="info" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Pending Orders
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatNumber(metrics.pendingOrders)}
                  </Typography>
                  <Typography variant="body2" color="warning.main" mt={1}>
                    Needs attention
                  </Typography>
                </Box>
                <People color="warning" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={3}>
        {/* Revenue Trend Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Revenue & Orders Trend</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#8884d8"
                    strokeWidth={2}
                    name="Revenue (₹)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="orders"
                    stroke="#82ca9d"
                    strokeWidth={2}
                    name="Orders"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Status Breakdown */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Order Status</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderStatusBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ status, percentage }) => `${status}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {orderStatusBreakdown.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Products and Customers */}
      <Grid container spacing={3} mb={3}>
        {/* Top Products */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Products</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="right">{formatNumber(product.quantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(product.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Customers */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Customers</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell align="right">{formatNumber(customer.totalOrders)}</TableCell>
                        <TableCell align="right">{formatCurrency(customer.totalRevenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Orders */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Recent Orders</Typography>
            <Button
              size="small"
              onClick={() => navigate('/admin/documents/sales-orders')}
            >
              View All
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.order_number}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/documents/sales-orders/${order.id}`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SalesDashboard;
